package com.evoapps.evoshift.viewmodels

import kotlinx.datetime.todayIn
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.currentOrNearest
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.of
import com.evoapps.evoshift.domain.usecase.ObserveRosterChangesUseCase
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine as combineFlows
import kotlinx.datetime.YearMonth
import kotlinx.datetime.plus
import kotlinx.datetime.LocalDate
import kotlinx.datetime.DatePeriod
import com.evoapps.evoshift.domain.util.calculateDurationHours
import com.evoapps.evoshift.domain.usecase.roster.GetRosterShiftsUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.ui.util.toNote
import com.evoapps.evoshift.location.LocationReader
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_user_not_found
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.BreakPolicy
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.workedHours
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.AtWorkEntry
import com.evoapps.evoshift.domain.usecase.GetManagerDashboardUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.domain.usecase.shift.ClockStatus
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.merge
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.receiveAsFlow
import com.evoapps.evoshift.domain.usecase.staff.GetTeamReadinessUseCase
import com.evoapps.evoshift.domain.usecase.staff.StaffAlerts
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now

data class ManagerDashboardUiState(
    /** SH-226: "Weather in the coming days" (off: no card). */
    val weather: UpcomingWeatherState = UpcomingWeatherState(),
    val isLoading: Boolean = true,
    val userName: String = "",
    val workplaceName: String = "",
    val nextShift: Shift? = null,
    /** SH-247: the manager's own shifts were read ([nextShift] null then means none, not unknown yet). */
    val ownKnown: Boolean = false,
    val activeEmployees: List<String> = emptyList(), // Names of employees currently working
    val upcomingEmployees: List<String> = emptyList(), // Names of employees working next
    val completedEmployees: List<String> = emptyList(), // Names of employees who finished working today
    val upcomingDate: String? = null,
    val pendingActionsCount: Int = 0,
    val openSkeletonSlotsCount: Int = 0,
    /** The manager's own clock-in button (managers work shifts too). */
    val clock: ClockStatus = ClockStatus.Idle,
    val clockBusy: Boolean = false,
    /** SH-106: the clock worked but the manager will see it flagged (outside the area, no location). */
    val clockNote: UiText? = null,
    /** SH-106: the one-time explanation of the location reading is open. */
    val locationNotice: Boolean = false,
    /** SH-165: clocking out well before the planned end ("HH:mm") waits for the person's answer. */
    val earlyClockOut: String? = null,
    /** SH-165: the clock-out just done, offered for undo in the snackbar. */
    val clockOutUndo: ClockOutUndo? = null,
    /** The month's planned and worked hours per day, for the hours chart. */
    val hoursByDay: List<DayHours> = emptyList(),
    /** Today's assigned shifts with clock-in/out and status, by planned start (SH-72, managers only). */
    val todayAtWork: List<AtWorkEntry> = emptyList(),
    /** What the person may do at this workplace: which cards show. */
    val capabilities: Set<Capability> = emptySet(),
    /** SH-47: people whose medical exam or OHS training expired or ends within 30 days. */
    val staffAlerts: StaffAlerts = StaffAlerts(),
    val error: UiText? = null,
    /** SH-188: the first load failed and nothing was shown yet: Try again instead of an empty dashboard. */
    val loadError: UiText? = null,
    /** Something has been shown; a later failure is a message over it, not Try again. */
    val loaded: Boolean = false
)

/** Planned and clocked hours of one day of the month. */
data class DayHours(val date: LocalDate, val planned: Double, val worked: Double)

class ManagerDashboardViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val getManagerDashboardUseCase: GetManagerDashboardUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val myShiftClock: MyShiftClock,
    /** For the hours chart; without it the dashboard shows the figures only. */
    private val getRosterShiftsUseCase: GetRosterShiftsUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val getTeamReadiness: GetTeamReadinessUseCase? = null,
    /** SH-89: the top bar's month, for the hours chart; without it the current month. */
    private val monthSelection: WorkplaceMonthSelection? = null,
    /** SH-136: the workplace's break policy, so the chart's clocked hours leave the unpaid break out. */
    private val workplaceRepository: WorkplaceRepository? = null,
    /** SH-188: the shown months' shift changes; without it the dashboard reads once. */
    private val observeRosterChanges: ObserveRosterChangesUseCase? = null,
    /** SH-226: the forecast card; without it (or with weather off) there is none. */
    observeWeather: com.evoapps.evoshift.domain.usecase.weather.ObserveWorkplaceWeatherUseCase? = null,
    today: () -> kotlinx.datetime.LocalDate = {
        com.evoapps.evoshift.domain.util.AppClock.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault())
    }
) : ViewModel() {

    private val upcomingWeather = UpcomingWeather(observeWeather, workplaceRepository, today)

    private val _uiState = MutableStateFlow(ManagerDashboardUiState())
    val uiState = _uiState.asStateFlow()

    private val watching = JobSlot()
    private var watched: Pair<String, Set<String>>? = null

    /**
     * SH-188: the months on screen are followed: a shift change (an assignment, a swap taken, a
     * clock-in, here or on another device) reads the dashboard again, quietly, behind what is
     * shown. So the team's day and the to-dos are current when the person comes back from the
     * roster or the requests.
     */
    private fun watch(workplaceId: String, rosterIds: Set<String>) {
        val observe = observeRosterChanges ?: return
        val key = workplaceId to rosterIds
        if (key == watched) return
        watched = key
        if (rosterIds.isEmpty()) {
            watching.cancel()
            return
        }
        watching.launch(viewModelScope) {
            rosterIds.map { observe(it, includeFinances = false) }.merge()
                .catch { e -> _uiState.update { it.copy(error = e.toUiText()) } }
                .collect { viewModelScope.launch { loadData(workplaceId, shownMonth) } }
        }
    }

    private val clockResultChannel = kotlinx.coroutines.channels.Channel<Boolean>(kotlinx.coroutines.channels.Channel.BUFFERED)

    /** SH-158: each clock, break or "without a shift" tap's outcome, once (true: done), for the screen's haptics. */
    val clockResults: kotlinx.coroutines.flow.Flow<Boolean> = clockResultChannel.receiveAsFlow()

    init {
        observeSession()
        observeOwnShifts()
    }

    /** The manager's own next shift at the current workplace and their clock-in button. */
    @OptIn(ExperimentalCoroutinesApi::class)
    private fun observeOwnShifts() {
        viewModelScope.launch {
            // Profile updates of the same person at the same workplace keep the listener.
            combine(getCurrentUserUseCase(), sessionManager.currentWorkplaceId) { user, wp -> user?.uid to wp }
                .distinctUntilChanged()
                .flatMapLatest { (uid, wp) ->
                    if (uid == null) flowOf(MyShiftClock.State()) else myShiftClock.observe(uid, wp)
                }
                // SH-188: a failed listener leaves the screen with a message, not an ended coroutine.
                .catch { e -> _uiState.update { it.copy(error = e.toUiText(), ownKnown = true) } }
                .collect { own -> _uiState.update { it.copy(clock = own.clock, nextShift = own.nextShift, ownKnown = true) } }
        }
    }

    /** The location reader of the screen (SH-106), kept while the one-time notice is open. */
    private var pendingLocationReader: LocationReader? = null

    /**
     * The clock button. Where the workplace checks the location (SH-106), the first tap on this
     * device explains why first (the locationNotice state); [readLocation] is then called once.
     */
    fun onClockClicked(readLocation: LocationReader? = null) = clockOrBreak(readLocation, breakButton = false)

    /** SH-116: the break button while working: starts a break, or ends the one going on. */
    fun onBreakClicked(readLocation: LocationReader? = null) = clockOrBreak(readLocation, breakButton = true)

    /** Which button waits for the location notice (SH-116: the clock or the break). */
    private var pendingBreak = false

    /** The location reader kept while the early clock-out question is open (SH-165). */
    private var earlyClockOutReader: LocationReader? = null

    private fun clockOrBreak(readLocation: LocationReader?, breakButton: Boolean) {
        val status = _uiState.value.clock
        // SH-165: a clock-out well before the planned end is asked about first.
        if (!breakButton && status is ClockStatus.Working && myShiftClock.isEarlyClockOut(status)) {
            earlyClockOutReader = readLocation
            _uiState.update { it.copy(earlyClockOut = clockTimeOf(status.shift.plannedTime.end)) }
            return
        }
        proceed(status, readLocation, breakButton)
    }

    /** SH-165: the answer to "Planned end 22:00. Clock out now?" ([confirmed]: clock out). */
    fun onEarlyClockOutAnswered(confirmed: Boolean) {
        val reader = earlyClockOutReader
        earlyClockOutReader = null
        _uiState.update { it.copy(earlyClockOut = null) }
        if (confirmed) proceed(_uiState.value.clock, reader, breakButton = false)
    }

    /** SH-165: Undo in the "Clocked out" snackbar: the shift is open again. */
    fun onUndoClockOut() {
        val undo = _uiState.value.clockOutUndo ?: return
        _uiState.update { it.copy(clockOutUndo = null) }
        viewModelScope.launch {
            val uid = getCurrentUserUseCase().first()?.uid ?: return@launch
            _uiState.update { it.copy(clockBusy = true) }
            val result = myShiftClock.undoClockOut(undo.shiftId, uid)
            _uiState.update { it.copy(clockBusy = false, error = result.exceptionOrNull()?.toUiText()) }
            clockResultChannel.trySend(result.isSuccess)
        }
    }

    /** SH-165: the "Clocked out" snackbar is gone without Undo. */
    fun dismissClockOutUndo() = _uiState.update { it.copy(clockOutUndo = null) }

    private fun proceed(status: ClockStatus, readLocation: LocationReader?, breakButton: Boolean) {
        viewModelScope.launch {
            if (myShiftClock.needsLocationNotice(status)) {
                pendingLocationReader = readLocation
                pendingBreak = breakButton
                _uiState.update { it.copy(locationNotice = true) }
                return@launch
            }
            clock(status, readLocation, breakButton)
        }
    }

    /** The person read the location notice ([accepted]) or closed it (nothing is clocked). */
    fun onLocationNoticeAnswered(accepted: Boolean) {
        val reader = pendingLocationReader
        pendingLocationReader = null
        _uiState.update { it.copy(locationNotice = false) }
        if (!accepted) return
        myShiftClock.acknowledgeLocationNotice()
        val status = _uiState.value.clock
        val breakButton = pendingBreak
        viewModelScope.launch { clock(status, reader, breakButton) }
    }

    private suspend fun clock(status: ClockStatus, readLocation: LocationReader?, breakButton: Boolean = false) {
        val uid = getCurrentUserUseCase().first()?.uid ?: return
        _uiState.update { it.copy(clockBusy = true, clockNote = null) }
        val result = myShiftClock.click(status, uid, readLocation, breakButton)
        val clockedOut = result.isSuccess && !breakButton && status is ClockStatus.Working
        _uiState.update {
            it.copy(
                clockBusy = false, error = result.exceptionOrNull()?.toUiText(), clockNote = result.getOrNull()?.toNote(),
                clockOutUndo = if (clockedOut) ClockOutUndo((status as ClockStatus.Working).shift.id) else it.clockOutUndo
            )
        }
        clockResultChannel.trySend(result.isSuccess)
    }

    fun clearClockNote() = _uiState.update { it.copy(clockNote = null) }

    fun clearError() = _uiState.update { it.copy(error = null) }

    private fun observeSession() {
        viewModelScope.launch {
            val selected = monthSelection?.month
            if (selected == null) {
                sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                    if (workplaceId != null) loadData(workplaceId, null)
                }
            } else {
                sessionManager.currentWorkplaceId.combineFlows(selected) { workplaceId, month -> workplaceId to month }
                    .collectLatest { (workplaceId, month) -> if (workplaceId != null) loadData(workplaceId, month) }
            }
        }
    }

    /** The month shown, for Try again. */
    private var shownMonth: YearMonth? = null

    /**
     * SH-188: a read that throws (a refused listener, offline without a cache) is the screen's
     * error, never an ended coroutine: the session is still followed afterwards.
     */
    private suspend fun loadData(workplaceId: String, month: YearMonth?) {
        shownMonth = month
        try {
            load(workplaceId, month)
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            failedToLoad(e)
        }
    }

    /** Nothing shown yet: Try again; over the team's day shown: a message. */
    private fun failedToLoad(e: Throwable) {
        _uiState.update {
            if (it.loaded) it.copy(isLoading = false, error = e.toUiText())
            else it.copy(isLoading = false, loadError = e.toUiText())
        }
    }

    /** SH-188: Try again after the first load failed. */
    fun retry() {
        val workplaceId = sessionManager.currentWorkplaceId.value ?: return
        _uiState.update { it.copy(loadError = null, isLoading = !it.loaded) }
        viewModelScope.launch { loadData(workplaceId, shownMonth) }
    }

    /** Only the first load shows the spinner; a workplace switch keeps the old figures until the new arrive. */
    private suspend fun load(workplaceId: String, month: YearMonth?) {
        val user = getCurrentUserUseCase().first()
        if (user == null) {
            _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
            return
        }

        val engagement = user.engagements[workplaceId]
        val workplaceName = engagement?.cachedWorkplaceName?.takeIf { it != "TBD" && it.isNotBlank() } .orEmpty()
        val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
        // SH-226: the forecast for the coming days (nothing is read with weather off).
        upcomingWeather.follow(viewModelScope, workplaceId) { weather -> _uiState.update { it.copy(weather = weather) } }

        _uiState.update {
            it.copy(
                userName = user.displayName,
                workplaceName = workplaceName,
                capabilities = capabilities
            )
        }
        // The team's day and the month's to-dos are the managers'; an ended engagement reads nothing.
        if (Capability.EDIT_ROSTER !in capabilities) {
            _uiState.update { it.copy(isLoading = false) }
            return
        }

        // SH-47: medical exams and OHS training expired or ending within 30 days (statuses only).
        val alerts = if (capabilities.shows(Gate.STAFF_STATUS)) getTeamReadiness?.alerts(workplaceId)?.getOrNull() else null
        _uiState.update { it.copy(staffAlerts = alerts ?: StaffAlerts()) }

        // Find current roster
        val now = now().toLocalDateTime(TimeZone.currentSystemDefault())
        val currentMonth = now.month.number
        val currentYear = now.year

        val rosters = getRosterSummaryUseCase(workplaceId).first()
        val targetRoster = rosters.currentOrNearest(currentYear, currentMonth)
        watch(workplaceId, setOfNotNull(targetRoster?.id, month?.let { rosters.of(it)?.id }))

        if (targetRoster != null) {
            // This dashboard shows no money (operational managers may not read it): never ask for it.
            val result = getManagerDashboardUseCase(targetRoster.id, includeFinances = false)
            result.onSuccess { data ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        loaded = true,
                        loadError = null,
                        // Mapping domain state to UI state
                        pendingActionsCount = data.pendingSwapsCount,
                        openSkeletonSlotsCount = data.openShiftsTodayCount,
                        activeEmployees = data.activeEmployees,
                        upcomingEmployees = data.upcomingEmployees,
                        completedEmployees = data.completedEmployees,
                        upcomingDate = data.upcomingDate,
                        todayAtWork = data.todayAtWork
                    )
                }
            }.onFailure { e -> failedToLoad(e) }
            // SH-89: the hours chart is the top bar's month; the team's day stays today's.
            val chartRoster = if (month != null) rosters.of(month) else targetRoster
            if (chartRoster == null) {
                _uiState.update { it.copy(hoursByDay = emptyList()) }
            } else {
                getRosterShiftsUseCase?.invoke(chartRoster.id)?.onSuccess { shifts ->
                    val breakPolicy = workplaceRepository?.getWorkplace(workplaceId)?.getOrNull()?.settings?.breakPolicy ?: BreakPolicy()
                    _uiState.update { it.copy(hoursByDay = hoursByDay(chartRoster.year, chartRoster.month, shifts, breakPolicy)) }
                }
            }
        } else {
            // Nothing to show for this workplace: drop the previous workplace's figures.
            _uiState.update {
                it.copy(
                    isLoading = false,
                    loaded = true,
                    loadError = null,
                    pendingActionsCount = 0,
                    openSkeletonSlotsCount = 0,
                    activeEmployees = emptyList(),
                    upcomingEmployees = emptyList(),
                    completedEmployees = emptyList(),
                    upcomingDate = null,
                    todayAtWork = emptyList(),
                    hoursByDay = emptyList()
                )
            }
        }
    }
}

/**
 * Every day of the month with its planned hours (assigned shifts) and clocked hours; SH-136: the
 * clocked hours leave out the unpaid break under [breakPolicy] (BreakRules, as labour cost and the
 * daily log count them).
 */
internal fun hoursByDay(year: Int, month: Int, shifts: List<Shift>, breakPolicy: BreakPolicy = BreakPolicy()): List<DayHours> {
    val byDate = shifts.filter { it.userId != null }.groupBy { it.date }
    return generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
        .takeWhile { it.month.number == month }
        .map { date ->
            val day = byDate[date.toString()].orEmpty()
            DayHours(
                date = date,
                planned = day.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) },
                worked = day.sumOf { shift -> shift.workedHours(breakPolicy) ?: 0.0 }
            )
        }
        .toList()
}

